import { TemplateResult, html, LitElement } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js'
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerStyles from './styles/outline-locations-nav.inner.pcss?inline';
import globalStyles from './styles/outline-locations-nav.global.pcss?inline';
import { SlotHelper } from '../../controllers/slot-helper';


import '../outline-mobile-menu/outline-mobile-menu';

/**
 * The OutlineLocationsNav component
 * @element outline-locations-nav
 * @slot - nav: drupal generated list markup
 * @slot - eyebrow: outline-heading configured to heading-size="eyebrow" font-family="body"
 * @slot - heading: outline-heading configured to heading-size="2xl"
 * @slot - description: outline-ckeditor
 * @slot - content: outline-ckeditor
 */
@customElement('outline-locations-nav')
export class OutlineLocationsNav extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  private _slotHelper = new SlotHelper(this);


  /**
   * Text to display in the button at mobile. 
   */
  @property({ type: String, attribute: 'label-text' })
  labelText: string;

  @property({type: Boolean, attribute: 'has-eyebrow'})
  hasEyebrow: boolean;

  @property({type: Boolean, attribute: 'has-headline'})
  hasHeadline: boolean;
  
  @property({type: Boolean, attribute: 'has-description'})
  hasDescription: boolean;

  @property({type: Boolean, attribute: 'has-content'})
  hasContent: boolean;
  
  @property({type: Boolean, attribute: 'open-on-load'})
  openOnLoad: boolean;

  @queryAssignedElements({slot: 'nav'})
  navRef: Array<HTMLElement>

  firstUpdated() {
    this.configureMarkup();
    this.handleActivePath();
  }

  render(): TemplateResult {
    this.hasEyebrow = this._slotHelper.isNotEmpty('eyebrow');
    this.hasHeadline = this._slotHelper.isNotEmpty('headline');
    this.hasDescription = this._slotHelper.isNotEmpty('description');
    this.hasContent = this._slotHelper.isNotEmpty('content');

    return html`
      <div class="outline-locations-nav">
        <div class="outline-locations-nav--nav-wrapper">
        <div class="desktop-label-wrapper">
          ${this.labelText}
        </div>
        <outline-mobile-menu svg-size="30px" close-icon="hamburger" open-icon="cross" variant="locations">
          <span slot="button-label">${this.labelText}</span>
          <slot name="nav"></slot>
        </outline-mobile-menu>
        </div>
        <div class="outline-locations-nav--content-wrapper">
          ${this.headlineTemplate()}
          ${this.descriptionTemplate()}
          ${this.contentTemplate()} 
        </div>
      </div>
    `;
  }

  headlineTemplate() {
    if (this.hasEyebrow || this.hasHeadline) {
      return html `
        <div class="heading-wrapper">
          <slot name="eyebrow"></slot>
          <slot name="heading"></slot>
        </div>`;
    } else return;
  }

  descriptionTemplate() {
    if (this.hasDescription) {
      return html `
        <div class="description-wrapper">
          <slot name="description"></slot>
        </div>`;
    } else return;
  }

  contentTemplate() {
    if (this.hasContent) {
      return html `
        <div class="content-wrapper">
          <slot name="content"></slot>
        </div>`;
    } else return 
  }


  handleActivePath() {
    const activeEl = this.querySelector('.is-active');
    if (activeEl) {                  
      this.getClosestLi(activeEl)?.classList.add('current');
      this.setActiveTrail(activeEl);
    }
  }

  getClosestLi = (el:Element) => el.parentElement!.closest('li');

  setActiveTrail(activeEl: Element) {    
    if (activeEl){
      const parentLi = this.getClosestLi(activeEl);
      if (parentLi) {
        parentLi.classList.add('active-path');       
        
        if(this.openOnLoad) {
          parentLi.setAttribute('open', 'open')
        } 

        if (!parentLi.classList.contains('top-level')) {
          this.setActiveTrail(parentLi);
        }
      }
    }    
  }

  subNavClickHandler(e: Event) {
    const button = e.target as HTMLButtonElement;
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const dataId = button.getAttribute('data-item-id');
    if (dataId) {
      const parentLi = document.querySelector(`#item-${dataId}`);
      if (parentLi && parentLi.nodeName === 'LI') {
        parentLi.toggleAttribute('open');
      }      
      button.setAttribute('aria-expanded', `${isExpanded ? 'false' : 'true'}`)
    }
  }

  configureMarkup() {
    const list = this.navRef[0]?.children[0];    
    const hasSub = [...list?.querySelectorAll('li')].filter(li => li.querySelector('ul'));
    
    [...list.children].forEach(li => li.classList.add('top-level'));

    hasSub.forEach(li => {
      const seed = Math.floor(Math.random() * 100000000).toString();
      const button = document.createElement('button'); 
      const wrapperSpan = document.createElement('div');
      const anchor = [...li.children].filter(child => child.nodeName === 'A')[0];

      // Configure button
      button.classList.add('location-button');
      button.setAttribute('aria-haspopup', 'true');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('data-item-id', `${seed}`);
      button.addEventListener('click', this.subNavClickHandler);

      // Wrap button and anchor in span
      wrapperSpan.appendChild(anchor);
      wrapperSpan.appendChild(button);

      // Configure li
      li.appendChild(wrapperSpan);
      li.setAttribute('id', `item-${seed}`)
      li.insertBefore(wrapperSpan, li.children[0]);
      li.classList.add('has-sub-menu');
    })
  }
}


/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    ['outline-locations-nav']: OutlineLocationsNav;
  }
}
